import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router'
import { Loader } from 'lucide-react'
import { AppSidebar } from '@/components/layout/app-sidebar'
import { SiteHeader } from '@/components/layout/site-header'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { useMemberRenewalStatus } from '@/features/membership/renewal'
import { useAuth } from '@/features/authentication/components/auth-provider'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { useEffect } from 'react'

export const Route = createFileRoute('/_authenticated')({
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    // If authentication is still being determined, defer auth checks for now.
    if (context.auth.isLoading) {
      return
    }

    if (!context.auth.isAuthenticated) {
      // If this transition comes immediately after an intentional logout,
      // avoid appending a redirect back to the protected page.
      if (typeof window !== 'undefined') {
        const justLoggedOut = window.sessionStorage.getItem('justLoggedOut')
        if (justLoggedOut) {
          try { window.sessionStorage.removeItem('justLoggedOut') } catch {}
          throw redirect({ to: '/login', search: {} })
        }
      }
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }

    // Enforce mandatory password change when flagged
    if (typeof window !== 'undefined') {
      const forceReset = window.localStorage.getItem('force_password_reset')
      if (forceReset && location.pathname !== '/force-password-reset') {
        throw redirect({ to: '/force-password-reset', search: {} })
      }
    }
  },
})

function RouteComponent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const isAdmin = isAdminUser(user) || isOrganisationAdminUser(user)
  const renewalStatus = useMemberRenewalStatus()

  const needsRenewalFlow =
    !isAdmin &&
    !renewalStatus.isLoading &&
    (renewalStatus.canRenew || renewalStatus.hasPendingRenewal)
  const isInRenewFlow = location.pathname.startsWith('/membership/renew')

  useEffect(() => {
    if (renewalStatus.isLoading) return

    // Use the same readiness flags as the Renew Membership button:
    // - canRenew (expired + no pending)
    // - hasPendingRenewal (submission already in review)
    if (needsRenewalFlow && !isInRenewFlow) {
      navigate({
        to: '/membership/renew',
        search: { pending: renewalStatus.hasPendingRenewal || undefined },
        replace: true,
      })
    }
  }, [
    needsRenewalFlow,
    isInRenewFlow,
    navigate,
  ])

  if (needsRenewalFlow && !isInRenewFlow) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader className="mr-2 animate-spin" />
        <p>Redirecting to renewal…</p>
      </div>
    )
  }

  return (
    <SidebarProvider>
      {/* Keep inset variant so SidebarInset applies margins and prevents overlap */}
      <AppSidebar />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
              <Outlet />
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
